import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, AfterViewChecked} from '@angular/core';
import {UserService} from "../../../../services/user.service";
import {AuthService} from "../../../../services/auth.service";
import * as UiActions from "../../../../state/ui/ui.actions";
import {Store} from "@ngrx/store";
import {animate, style, transition, trigger} from "@angular/animations";
import {filter, Observable, Subscription, take, takeUntil} from "rxjs";
import {assistantHistory, askAssistant, clearAskAssistantResponse} from "../../../../state/search/search.actions";
import {
  selectAssistantHistorySuccess,
  selectAssistantHistoryFailure,
  selectAskAssistantSuccess,
  selectAskAssistantFailure
} from "../../../../state/search/search.selectors";
import {AppState} from "../../../../state/app.state";
import {Subject} from "rxjs";

@Component({
  selector: 'app-assistant',
  templateUrl: './assistant.component.html',
  styleUrls: ['./assistant.component.scss'],
  animations: [
    trigger('fadeFromRight', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateX(20px)'
        }),
        animate('300ms ease-out', style({
          opacity: 1,
          transform: 'translateX(0)'
        }))
      ])
    ])
  ]
})
export class AssistantComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {

  @Input() aside$: Observable<boolean>;

  @Input() report:any;
  @Input() meetingAgendaSteps:any;
  @Input() icebreakers:any;
  @Output() dataEmitter: EventEmitter<any> = new EventEmitter();

  @ViewChild('messagesContainer') private messagesContainer: ElementRef;

  messages: { user_id: number, prompt: string, message: string, timestamp?: any }[] = [];
  message: string = '';
  others = [
    { id: 1, title: 'Show a list of' + 's interests', description: 'to use as icebreakers when starting the meeting', href: '#' },
    { id: 2, title: 'Propose a meeting agenda', description: 'to talk about the product you want to sell', href: '#' },
    { id: 3, title: 'Show a list of small talk ideas', description: 'to fill in the gaps and lighten the mood', href: '#' }
  ]
  edit: boolean = false
  id: string;
  prompt: string = "";
  assistantVisible: boolean = false;
  aiResponses: any = {
    person: [],
    company: [],
  };
  aiResponsesForCompany: boolean = false;
  company: any = false;
  loading: boolean = false;
  isLoadingHistory: boolean = false;
  company_expertise: string;
  prospect_insights: string;

  user:any;

  // Subscriptions management
  private destroy$ = new Subject<void>();
  private historySubscription: Subscription = null;
  private shouldScrollToBottom = false;
  private pendingMessage: { user_id: number, prompt: string, message: string, timestamp?: any } | null = null;
  private currentRequestId: string | null = null;

  constructor(
    private store: Store<AppState>,
    public userService: UserService,
    private authService: AuthService,
  ) {

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

  }

  ngOnInit(): void {
    // Subscribe to history changes
    this.setupHistorySubscription();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    // Clear component state on destroy
    this.clearComponentState();
    // Clear store state
    this.store.dispatch(clearAskAssistantResponse());

    this.destroy$.next();
    this.destroy$.complete();

    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }
  }

  /**
   * Clear all component state
   * Called when switching reports or destroying component
   */
  private clearComponentState(): void {
    this.messages = [];
    this.message = '';
    this.prompt = '';
    this.assistantVisible = false;
    this.aiResponses = {
      person: [],
      company: [],
    };
    this.aiResponsesForCompany = false;
    this.company = false;
    this.loading = false;
    this.isLoadingHistory = false;
    this.pendingMessage = null;
    this.currentRequestId = null;
    this.edit = false;
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
      }
    } catch (err) {
      console.error('[Assistant] Scroll error:', err);
    }
  }

  private setupHistorySubscription(): void {
    // Listen for history updates - only when we don't have a pending message
    this.store.select(selectAssistantHistorySuccess)
      .pipe(
        filter(history => {
          // Don't update if we have a pending message (user just sent a question)
          if (this.pendingMessage) {
            return false;
          }
          return history != null && Array.isArray(history);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (history) => {
          this.parseAndDisplayHistory(history);
        },
        error: (error) => {
          console.error('[Assistant] History subscription error:', error);
          this.isLoadingHistory = false;
        }
      });

    // Listen for errors
    this.store.select(selectAssistantHistoryFailure)
      .pipe(
        filter(error => error !== null),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (error) => {
          console.error('[Assistant] History failed:', error);
          this.isLoadingHistory = false;
        }
      });
  }

  private parseAndDisplayHistory(history: any[]): void {
    if (!history || !Array.isArray(history)) {
      console.warn('[Assistant] Invalid history data');
      this.isLoadingHistory = false;
      return;
    }

    // Parse and sort messages in chronological order (oldest first)
    const parsedMessages = history.map((item: any) => {
      // Extract the prompt by removing the prefix
      let cleanPrompt = item.prompt || '';
      if (cleanPrompt.startsWith('This data you have represents a person. Question: ')) {
        cleanPrompt = cleanPrompt.replace('This data you have represents a person. Question: ', '');
      } else if (cleanPrompt.startsWith('This data you have represents a company. Question: ')) {
        cleanPrompt = cleanPrompt.replace('This data you have represents a company. Question: ', '');
      }

      // Extract the message content from the response structure
      let messageContent = '';
      if (item.response && typeof item.response === 'object') {
        if (item.response.message && item.response.message.content) {
          messageContent = item.response.message.content;
        } else if (item.response.content) {
          messageContent = item.response.content;
        }
      } else if (typeof item.response === 'string') {
        messageContent = item.response;
      }

      return {
        user_id: this.user?.id || 1,
        prompt: cleanPrompt,
        message: messageContent,
        timestamp: item.created_at || item.timestamp || new Date()
      };
    }).sort((a, b) => {
      // Sort by timestamp to ensure chronological order (oldest first)
      return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    });

    // If we have a pending message (just sent), append it to the messages
    if (this.pendingMessage) {
      this.messages = [...parsedMessages, this.pendingMessage];
    } else {
      this.messages = parsedMessages;
    }

    this.isLoadingHistory = false;

    // Scroll to bottom when new messages are added or on initial load
    if (this.messages.length > 0) {
      this.shouldScrollToBottom = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When report Input changes, load history if we have both user and report
    if (changes['report']) {
      // Clear previous report's data immediately
      this.clearComponentState();

      // Clear store state
      this.store.dispatch(clearAskAssistantResponse());

      // Load new report's history if we have a valid report ID
      if (this.report?.id) {
        // Check if user is available, if not wait for it
        if (this.user) {
          this.getAssistantHistory();
        } else {
          // Subscribe to user once and then load history
          this.authService.authUser()
            .pipe(take(1))
            .subscribe(user => {
              this.user = user;
              if (user) {
                this.getAssistantHistory();
              }
            });
        }
      }
    }
  }

  getAssistantHistory() {
    this.isLoadingHistory = true;

    // Dispatch action - the subscription in setupHistorySubscription will handle the response
    this.store.dispatch(assistantHistory({
      search_id: this.report.id,
      search_company_id: this.report.search_company_id
    }));
  }

  showMeetingAgenda() {
    // Prevent multiple simultaneous requests
    if (this.loading) {
      return;
    }

    // Set the prompt to the predefined meeting agenda question
    this.prompt = 'Propose a meeting agenda';

    // Call the existing method which handles:
    // - Loading state
    // - Pending message with "Thinking..." state
    // - Store dispatch for persistence
    // - Response handling
    // - Auto-scroll
    // - Timestamps
    // - Proper user ID
    this.askAIAssistant();
  }

  askAIAssistant(forCompany = false, given_prompt = '') {
    this.loading = true;
    this.assistantVisible = true;

    let data = this.report.search_person.enhanced_data;

    if(this.prompt == '' && given_prompt != '')
      this.prompt = given_prompt

    const currentPrompt = this.prompt;

    // IMPORTANT: Clear the previous response from state first
    this.store.dispatch(clearAskAssistantResponse());

    // Generate unique request ID
    const requestId = Date.now().toString() + Math.random().toString(36);
    this.currentRequestId = requestId;

    // Create pending message object
    this.pendingMessage = {
      user_id: this.user.id,
      prompt: currentPrompt,
      message: 'Thinking...',
      timestamp: new Date()
    };

    // Add pending message to UI immediately
    this.messages.push(this.pendingMessage);

    // Scroll to show the new message
    this.shouldScrollToBottom = true;

    let prompt = "This data you have represents a person. Question: " + this.prompt;

    if(forCompany){
      prompt = "This data you have represents a company. Question: " + this.prompt;
      this.aiResponsesForCompany = true;
      data = this.report.search_person.enhanced_data.organization;
      data.company_expertise = this.company_expertise;
    } else {
      if (!Object.isExtensible(data)) {
        data = {...data, person_insights: this.prospect_insights};
      } else {
        data.person_insights = this.prospect_insights;
      }
      this.aiResponsesForCompany = false;
    }

    // Dispatch NgRx action
    this.store.dispatch(askAssistant({
      prompt: prompt,
      data: data,
      search_id: this.report.id,
      search_company_id: this.report.search_company_id
    }));

    // Subscribe to response - use takeUntil for proper cleanup
    // Skip the first emission to avoid stale data, then take the next one
    this.store.select(selectAskAssistantSuccess).pipe(
      filter(response => {
        return response != null;
      }),
      // Skip any emissions that happened before our request
      filter(() => {
        const isCurrentRequest = this.currentRequestId === requestId;
        return isCurrentRequest;
      }),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        // Only process if this is still the current request
        if (this.currentRequestId !== requestId) {
          return;
        }

        const responseContent = response.message?.content || response;

        // Update the pending message in the messages array with the actual response
        if (this.pendingMessage && this.pendingMessage.prompt === currentPrompt) {
          // Find the message in the array and update it
          const messageIndex = this.messages.findIndex(m =>
            m.prompt === currentPrompt && m.message === 'Thinking...'
          );

          if (messageIndex !== -1) {
            this.messages[messageIndex] = {
              ...this.messages[messageIndex],
              message: responseContent
            };
          }

          // Also update the pendingMessage reference
          this.pendingMessage.message = responseContent;
        }

        const responseObject = {
          prompt: currentPrompt,
          response: responseContent,
        };

        if(forCompany){
          this.aiResponses.company.unshift(responseObject);
        } else {
          this.aiResponses.person.unshift(responseObject);
        }

        this.prompt = "";
        this.loading = false;

        // Scroll to show the response
        this.shouldScrollToBottom = true;

        // Clear pending message - no need to reload history since we have the response
        this.pendingMessage = null;
        this.currentRequestId = null;
      },
      error: (error) => {
        console.error('[Assistant] Ask assistant error:', error);
        this.loading = false;
        this.pendingMessage = null;
        this.currentRequestId = null;
        // Remove the "Thinking..." message on error
        this.messages = this.messages.filter(m => m.message !== 'Thinking...');
      }
    });

    this.store.select(selectAskAssistantFailure).pipe(
      filter(failure => failure !== null && this.currentRequestId === requestId),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (error) => {
        console.error('[Assistant] Ask assistant failed:', error);
        this.loading = false;
        this.pendingMessage = null;
        this.currentRequestId = null;
        // Remove the "Thinking..." message on error
        this.messages = this.messages.filter(m => m.message !== 'Thinking...');
      }
    });
  }
  formatTimestamp(timestamp: any): string {
    if (!timestamp) return '';

    const date = new Date(timestamp);

    // Format: DD.MM.YYYY H:i
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  closeAside(): void {
    // Clear component state when closing aside
    this.clearComponentState();
    // Clear store state
    this.store.dispatch(clearAskAssistantResponse());
    // Close the aside
    this.store.dispatch(UiActions.toggleAside({ isOpen: false }));
  }

}
